import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangleIcon, Loader2Icon } from "lucide-react";

import { Card } from "@component/card";
import { useUserProfileContext } from "@context/user_profile";
import { getSubscriptionActive } from "@service/subscription";
import { SubscriptionActive } from "@type/subscription";
import { catchErrorLog } from "@util/sentry";
import { parseFormat } from "@util/date";
import BillingPaymentMethod from "@container/billing-payment-method";
import BillingPlans from "@container/billing-plans";
import {
    resolveBillingPlatform,
    STRIPE_SUBSCRIPTION_ENABLED,
} from "@util/billing-platform";

// The "Payment Details" surface — Current Subscription + Payment Method + Plans.
// Self-contained: resolves the billing platform from the signed-in user, loads
// the active subscription, and renders the three sections. Shared by the
// Subscription page (its "Payment Details" tab) and the Billing menu's "Plan
// Overview" tab, so it appears in both places without duplicating the logic.
//
// `showCurrentSubscription` (default true) toggles the "Current Subscription"
// block — the Billing > Plan Overview tab hides it (its plan/period detail is
// already covered there), while the Subscription page keeps it.
export default function BillingPaymentDetails({
    showCurrentSubscription = true,
    plansFirst = false,
}: {
    showCurrentSubscription?: boolean;
    plansFirst?: boolean;
} = {}) {
    const [, userInfo] = useUserProfileContext();
    const platform = useMemo(() => resolveBillingPlatform(userInfo), [userInfo]);

    const [subscription, setSubscription] = useState<SubscriptionActive | null>(null);
    const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
    // Distinguish a real load failure from "no subscription" — a transient
    // 500/network/auth error must NOT read as "the company has no subscription".
    const [subscriptionLoadError, setSubscriptionLoadError] = useState(false);

    // Refetch the active subscription. The Plans slice calls it after a
    // subscribe/change/cancel to reflect the new state.
    const loadSubscription = useCallback((isMounted?: () => boolean) => {
        const stillActive = () => (isMounted ? isMounted() : true);
        setIsLoadingSubscription(true);
        setSubscriptionLoadError(false);
        return getSubscriptionActive()
            .then((resp) => {
                if (!stillActive()) return;
                if (resp?.status === 200) {
                    setSubscription(resp.data?.data ?? null);
                    return;
                }
                setSubscriptionLoadError(true);
            })
            .catch((err: unknown) => {
                if (stillActive()) setSubscriptionLoadError(true);
                catchErrorLog(
                    `[billing-payment-details] active subscription load failed: ${
                        err instanceof Error ? err.message : String(err)
                    }`,
                );
            })
            .finally(() => {
                if (stillActive()) setIsLoadingSubscription(false);
            });
    }, []);

    useEffect(() => {
        if (!STRIPE_SUBSCRIPTION_ENABLED || !platform) return;
        let active = true;
        loadSubscription(() => active);
        return () => {
            active = false;
        };
    }, [platform, loadSubscription]);

    // Currency + current-plan context for the Plans slice (price display only).
    const billingCurrency =
        subscription?.subscription_currency || subscription?.company_currency || null;
    const currentPackageId = subscription?.subscription_package_id ?? null;
    // Stripe-backed active subscription → change/cancel; trial/legacy → subscribe.
    const hasActiveSubscription = Boolean(
        subscription &&
            subscription.is_active &&
            !subscription.is_expired &&
            !subscription.is_trial &&
            subscription.subscription_details?.stripe?.stripe_subscription_id,
    );
    const isCancelling = Boolean(
        subscription?.subscription_details?.stripe?.cancel_at_period_end ||
            subscription?.cancel_at_period_end ||
            subscription?.subscription_status === "cancelling",
    );
    // "Access expiring in …" note for the current (trial, non-expired) plan card.
    const accessExpiry = (() => {
        if (
            !subscription?.is_trial ||
            subscription.is_expired ||
            !subscription.ended_at
        ) {
            return null;
        }
        const diffMs = new Date(subscription.ended_at).getTime() - Date.now();
        if (Number.isNaN(diffMs)) return null;
        const daysRemaining = Math.ceil(diffMs / 86_400_000);
        if (daysRemaining < 0) return null;
        return {
            daysRemaining,
            expiryDate: parseFormat(subscription.ended_at, "D MMM YYYY"),
        };
    })();

    // Off (default) ⇒ the subscription/payment surface isn't available; render
    // nothing so the host (e.g. the Plan Overview tab) just shows its own content.
    if (!STRIPE_SUBSCRIPTION_ENABLED || !platform) return null;

    const paymentMethodSection = (
        <section className="flex flex-col gap-6 lg:flex-row lg:gap-8">
            <div className="lg:w-80 lg:flex-shrink-0">
                <h2 className="text-xl font-semibold text-gray-900">
                    Payment Method
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                    The card we charge for your subscription.
                </p>
            </div>
            <div className="flex-1 min-w-0">
                <BillingPaymentMethod />
            </div>
        </section>
    );

    const plansSection = (
        <section className="flex flex-col gap-6 lg:flex-row lg:gap-8">
            <div className="lg:w-80 lg:flex-shrink-0">
                <h2 className="text-xl font-semibold text-gray-900">
                    Pricing Plan
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                    Manage your pricing and plan features.
                </p>
            </div>
            <div className="flex-1 min-w-0">
                <BillingPlans
                    platform={platform}
                    currency={billingCurrency}
                    currentPackageId={currentPackageId}
                    hasActiveSubscription={hasActiveSubscription}
                    isCancelling={isCancelling}
                    accessExpiry={accessExpiry}
                    onSubscriptionChanged={loadSubscription}
                />
            </div>
        </section>
    );

    return (
        <div className="space-y-8">
            {/* Current subscription — hidden on the Billing > Plan Overview tab. */}
            {showCurrentSubscription && (
                <>
                    <section className="flex flex-col gap-6 lg:flex-row lg:gap-8">
                        <div className="lg:w-80 lg:flex-shrink-0">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Current Subscription
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Your active plan and renewal details.
                            </p>
                        </div>
                        <div className="flex-1 min-w-0">
                            {isLoadingSubscription ? (
                                <Card className="p-6">
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <Loader2Icon className="h-4 w-4 animate-spin" />
                                        Loading subscription…
                                    </div>
                                </Card>
                            ) : subscriptionLoadError ? (
                                <Card className="p-6">
                                    <p className="text-sm text-red-600">
                                        We couldn't load your current subscription right now. Please try again.
                                    </p>
                                </Card>
                            ) : subscription ? (
                                <CurrentSubscriptionCard subscription={subscription} />
                            ) : (
                                <Card className="p-6">
                                    <p className="text-sm text-gray-500">
                                        No active subscription found for this workspace.
                                    </p>
                                </Card>
                            )}
                        </div>
                    </section>

                    <div className="border-t border-gray-200" />
                </>
            )}

            {/* Payment Method + Plans — order depends on `plansFirst` (the Billing
                > Plan Overview tab puts Plans at the top). */}
            {plansFirst ? plansSection : paymentMethodSection}

            <div className="border-t border-gray-200" />

            {plansFirst ? paymentMethodSection : plansSection}
        </div>
    );
}

function CurrentSubscriptionCard({
    subscription,
}: {
    subscription: SubscriptionActive;
}) {
    const name = subscription.subscription_name || subscription.subscription_package_name || "Current plan";
    const renewalDate = subscription.next_billing_date || subscription.ended_at;

    // Extra billing details — surfaced only when the BE provides them (no fabrication).
    const billingStart = subscription.billing_period_start;
    const billingEnd = subscription.billing_period_end;
    const nextPayment = subscription.next_payment_date;
    const cadence = subscription.payment_cadence;
    const periode = subscription.periode;
    const daysRemaining = (() => {
        if (!renewalDate) return null;
        const diffMs = new Date(renewalDate).getTime() - Date.now();
        if (Number.isNaN(diffMs)) return null;
        const days = Math.ceil(diffMs / 86_400_000);
        return days >= 0 ? days : null;
    })();

    // A Stripe subscription set to cancel at period end stays active until
    // billing_period_end, then lapses — the user keeps access until then.
    const isCancelling = Boolean(
        subscription.subscription_details?.stripe?.cancel_at_period_end ||
            subscription.cancel_at_period_end ||
            subscription.subscription_status === "cancelling",
    );
    const cancelDate = billingEnd || subscription.ended_at;

    const statusChip = subscription.is_expired
        ? { label: "Expired", className: "bg-rose-50 text-rose-700 border-rose-200" }
        : subscription.is_trial
            ? { label: "Trial", className: "bg-amber-50 text-amber-700 border-amber-200" }
            : isCancelling
                ? { label: "Cancelling", className: "bg-amber-50 text-amber-700 border-amber-200" }
                : { label: "Active", className: "bg-emerald-50 text-emerald-700 border-emerald-200" };

    return (
        <Card className="p-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-gray-500">
                        Plan
                    </p>
                    <p className="text-lg font-semibold text-gray-900 mt-0.5">{name}</p>
                </div>
                <span
                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusChip.className}`}
                >
                    {statusChip.label}
                </span>
            </div>

            {isCancelling && !subscription.is_expired && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                    <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                    <p className="text-sm text-amber-800">
                        Your subscription is set to cancel and will end on{" "}
                        <strong>
                            {cancelDate
                                ? parseFormat(cancelDate, "D MMM YYYY")
                                : "the end of the current billing period"}
                        </strong>
                        . You'll keep full access until then.
                    </p>
                </div>
            )}

            <dl className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                    <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
                        Started
                    </dt>
                    <dd className="text-sm text-gray-900 mt-0.5">
                        {subscription.started_at
                            ? parseFormat(subscription.started_at, "D MMM YYYY")
                            : "—"}
                    </dd>
                </div>

                {(billingStart || billingEnd) && (
                    <div>
                        <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
                            Billing period
                        </dt>
                        <dd className="text-sm text-gray-900 mt-0.5">
                            {billingStart ? parseFormat(billingStart, "D MMM YYYY") : "—"}
                            {" – "}
                            {billingEnd ? parseFormat(billingEnd, "D MMM YYYY") : "—"}
                        </dd>
                    </div>
                )}

                {/* Renewal/charge fields don't apply once the subscription is set
                    to cancel — the cancellation note above already shows the end
                    date (#2737 follow-up). */}
                {!isCancelling && (
                    <div>
                        <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
                            {subscription.is_trial ? "Ends" : "Next billing date"}
                        </dt>
                        <dd className="text-sm text-gray-900 mt-0.5">
                            {renewalDate ? parseFormat(renewalDate, "D MMM YYYY") : "—"}
                            {daysRemaining != null && (
                                <span className="text-gray-400">
                                    {" "}
                                    ({daysRemaining} {daysRemaining === 1 ? "day" : "days"} remaining)
                                </span>
                            )}
                        </dd>
                    </div>
                )}

                {(periode || cadence) && (
                    <div>
                        <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
                            Pricing plan
                        </dt>
                        <dd className="text-sm text-gray-900 mt-0.5">
                            {periode || name}
                            {cadence && (
                                <span className="text-gray-400"> ({cadence})</span>
                            )}
                        </dd>
                    </div>
                )}

                {/* A trial has no charge scheduled yet, so the "Next payment date"
                    is meaningless — the trial end is already shown by "Ends". */}
                {nextPayment && !isCancelling && !subscription.is_trial && (
                    <div>
                        <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
                            Next payment date
                        </dt>
                        <dd className="text-sm text-gray-900 mt-0.5">
                            {parseFormat(nextPayment, "D MMM YYYY")}
                        </dd>
                    </div>
                )}
            </dl>
        </Card>
    );
}
